import { type GSAFacetDTO, type GSASearchResultsDTO } from "~/GearboxSelectionAssistant/dtos";
import {
  type FacetFilterDefinition,
  type ResultCount,
  type TechFilterDefinition,
} from "~/GearboxSelectionAssistant/models";
import { toMapFn } from "~/utils/reducers";
import { TECH_VALUE_CODE_LIST } from "~/GearboxSelectionAssistant/components/DimensionsFilter/constants";
import {
  CHARACTERISTIC_CODE_LIST,
  MULTIVALUE_MAP,
} from "~/GearboxSelectionAssistant/components/CharacteristicsFilter/constants";
import { transformSorts } from "~/Search/transformers/SearchResultDataTransformer";
import { splitSortingCode } from "~/utils/misc";

export function extractAllData(dto: GSASearchResultsDTO) {
  const facetByCode = toMapFn([...dto.facets, ...(dto.otherFacets ?? [])], (facet) => facet.code);
  const relevantDimensionFacets = TECH_VALUE_CODE_LIST.map((code) => facetByCode[code]).filter(Boolean);
  const relevantCharacteristicFacets = CHARACTERISTIC_CODE_LIST.map((code) => facetByCode[code]).filter(Boolean);
  const mapByCode = <T extends { code: string }>(list: T[]) => toMapFn(list, (item) => item.code);

  const selectedSort = dto.sorts.find((sort) => sort.selected);
  const { code, sortingDirection } = splitSortingCode(selectedSort?.code ?? "");

  return {
    dimensionDefinitions: mapByCode(relevantDimensionFacets.map(extractTechFilterDefinition)),
    characteristicDefinitions: mapByCode(relevantCharacteristicFacets.map(extractCharacteristicDefinition)),
    resultCount: toMapFn(
      [...relevantCharacteristicFacets, ...relevantDimensionFacets].map(extractResultCount),
      (item) => item.code,
      (item) => item.resultCount,
    ),
    characteristicValues: toMapFn(
      relevantCharacteristicFacets,
      (facet) => facet.code,
      extractCharacteristicSelectedValues,
    ),
    sortOptions: transformSorts(dto.sorts),
    selectedSortCode: code,
    selectedSortDirection: sortingDirection ?? undefined,
  };
}

function extractCharacteristicSelectedValues(dto: GSAFacetDTO): string[] {
  return dto.values.filter((value) => value.selected).map((value) => value.code);
}

export function extractCharacteristicDefinition({ name, ...dto }: GSAFacetDTO): FacetFilterDefinition {
  return {
    label: name,
    ...dto,
    multivalued: (MULTIVALUE_MAP as Record<string, boolean>)[dto.code] ?? false,
  };
}
export function extractResultCount(dto: GSAFacetDTO): { code: string; resultCount: ResultCount } {
  return {
    code: dto.code,
    resultCount: toMapFn(
      dto.values,
      (v) => v.code,
      (v) => v.count,
    ),
  };
}

export function extractTechFilterDefinition(dto: GSAFacetDTO): TechFilterDefinition {
  const [min, max] = findMinMax(dto);
  return {
    code: dto.code,
    min,
    max,
  };
}

function findMinMax(dto: GSAFacetDTO): [number, number] {
  const values = dto.values.map((value) => +value.code);
  values.sort((a, b) => a - b);
  return [values[0], values[values.length - 1]];
}
